import { ChangeEvent, useContext } from 'react'
import {
  mdiPencilOutline,
  mdiCheck,
  mdiClose,
  mdiAlertCircleOutline,
} from '@mdi/js'
import { useIntl } from 'react-intl'

import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'
import { SpinnerCentered } from 'components/Loader'
import { commonMessages } from 'app/i18n/messages'
import {
  NotesEditModeButton,
  NotesContainer,
  NotesStyledTextarea,
  NotesButtonsContainer,
  NotesSubmitButton,
  NotesRejectButton,
  NotesStyledIcon,
  NotesEditorContainer,
  NotesText,
  NotesTooltip,
  NotesInfoWrapper,
} from './styles'

export interface NoteCellProps {
  initialNote: string | null
  note: string
  editMode: boolean
  editDisabled?: boolean
  handleEditModeClick: () => void
  noteIsUpdating: boolean
  showError: boolean
  handleSubmit: () => void
  handleChange: (e: ChangeEvent<any>) => void
  handleReject: () => void
  submitDisabled: boolean
  usePermissionsCheck?: boolean
}

function NoteCell({
  initialNote,
  note,
  editMode,
  editDisabled = false,
  handleEditModeClick,
  noteIsUpdating,
  showError,
  handleSubmit,
  handleChange,
  handleReject,
  submitDisabled,
  usePermissionsCheck = true,
}: NoteCellProps) {
  const { formatMessage } = useIntl()
  const { hasRootUserPermission } = useContext(CurrentUserPermissionsContext)
  const canEditLicense = usePermissionsCheck ? hasRootUserPermission : true

  return (
    <NotesContainer>
      {showError && (
        <NotesInfoWrapper>
          <NotesStyledIcon
            path={mdiAlertCircleOutline}
            title={formatMessage(commonMessages.commonError)}
            description={formatMessage(commonMessages.commonError)}
            size={1}
          />
        </NotesInfoWrapper>
      )}

      {noteIsUpdating && (
        <NotesInfoWrapper>
          <SpinnerCentered size="24" />
        </NotesInfoWrapper>
      )}

      {!noteIsUpdating && !showError && (
        <NotesEditorContainer>
          {editMode ? (
            <NotesStyledTextarea
              value={note}
              onChange={(e) => handleChange(e)}
              disabled={!editMode}
              maxLength={1000}
            />
          ) : (
            <NotesText>
              {initialNote && (
                <>
                  {initialNote}
                  <NotesTooltip>{initialNote}</NotesTooltip>
                </>
              )}
            </NotesText>
          )}

          <NotesButtonsContainer editMode={editMode}>
            <NotesSubmitButton onClick={handleSubmit} disabled={submitDisabled}>
              <NotesStyledIcon path={mdiCheck} />
            </NotesSubmitButton>
            <NotesRejectButton onClick={handleReject}>
              <NotesStyledIcon path={mdiClose} />
            </NotesRejectButton>
          </NotesButtonsContainer>
        </NotesEditorContainer>
      )}
      {canEditLicense && !editDisabled && (
        <NotesEditModeButton onClick={handleEditModeClick}>
          <NotesStyledIcon path={mdiPencilOutline} />
        </NotesEditModeButton>
      )}
    </NotesContainer>
  )
}

export default NoteCell
